import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, MessageCircle } from "lucide-react";
import { ATMO, waLink } from "@/config/site";

const EASE = [0.22, 1, 0.36, 1];

export const HeroIntro = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.04, 1.16]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-38%"]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] min-h-[620px] overflow-hidden bg-[#17251C]"
    >
      {/* Bild erscheint nach der Linien-Animation */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        initial={{ opacity: 0, filter: "blur(14px)" }}
        animate={{ opacity: 1, filter: "blur(0px)" }}
        transition={{ duration: 1.9, delay: 1.15, ease: EASE }}
        className="absolute inset-0"
      >
        <img
          src={ATMO.hero}
          alt="Moderner Gartenraum mit hohen Formhecken, Rasenfläche und Designsessel in der Abenddämmerung"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#17251C] via-[#17251C]/58 to-[#17251C]/20" />

      {/* organische Linie zeichnet sich über den Screen */}
      <svg
        viewBox="0 0 1440 900"
        className="pointer-events-none absolute inset-0 h-full w-full"
        aria-hidden="true"
        preserveAspectRatio="xMidYMid slice"
      >
        <motion.path
          d="M-40 760C220 690 300 430 520 360 760 284 860 520 1080 430 1240 364 1330 240 1500 180"
          fill="none"
          stroke="#DCCDB4"
          strokeWidth="1.2"
          strokeOpacity="0.7"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1, strokeOpacity: [0.7, 0.7, 0.22] }}
          transition={{ duration: 2.6, ease: "easeInOut", times: [0, 0.6, 1] }}
        />
        <motion.path
          d="M520 360c-26-40-10-86 34-102 8 54-8 82-34 102Z"
          fill="#DCCDB4"
          initial={{ opacity: 0, scale: 0.4 }}
          animate={{ opacity: 0.35, scale: 1 }}
          transition={{ duration: 1.3, delay: 1.5, ease: EASE }}
          style={{ transformOrigin: "540px 380px" }}
        />
      </svg>

      <motion.div
        style={{ y: textY }}
        className="relative mx-auto flex h-full max-w-[1480px] flex-col justify-end px-5 pb-16 md:px-10 md:pb-24"
      >
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="eyebrow !text-[#DCCDB4]"
        >
          Hanse Garten Bremen
        </motion.p>

        <h1 className="display-xl mt-4 text-[#F3EDE2]">
          {["Räume,", "die wachsen."].map((line, i) => (
            <span key={line} className="line-mask">
              <motion.span
                className="block text-[15.5vw] leading-[0.88] sm:text-[12vw] lg:text-[8.6vw]"
                initial={{ y: "110%", opacity: 0 }}
                animate={{ y: "0%", opacity: 1 }}
                transition={{ duration: 1.25, delay: 1.55 + i * 0.16, ease: EASE }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 2.1, ease: EASE }}
          className="mt-8 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between"
        >
          <p className="max-w-lg text-base leading-relaxed text-[#DCCDB4] md:text-lg">
            Wir gestalten Gärten, die nicht nur gut aussehen – sondern sich richtig anfühlen.
          </p>

          <div className="flex flex-wrap gap-3">
            <Link to="/kontakt" className="btn-primary !bg-[#F3EDE2] !text-[#17251C]" data-testid="hero-cta-project">
              Projekt besprechen <ArrowDownRight size={15} />
            </Link>
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline on-dark"
              data-testid="hero-cta-consultation"
            >
              <MessageCircle size={15} /> Kostenlose Beratung
            </a>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.6, duration: 1 }}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 md:flex"
      >
        <span className="text-[0.58rem] uppercase tracking-[0.3em] text-[#DCCDB4]/70">Scrollen</span>
        <motion.span
          animate={{ scaleY: [0.2, 1, 0.2], originY: 0 }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          className="block h-10 w-[1px] bg-[#DCCDB4]/60"
        />
      </motion.div>
    </section>
  );
};
